/**
 * RBAC-ENF1 · 操作日志页「权限影子日志」：RBAC_ENFORCE=shadow 期间，
 * 按新权限点会被拦、但这次照旧放行的请求。切 enforce 前据此对账角色权限（谁缺哪个权限点、被打了多少次）。
 * 数据：GET /api/v1/rbac/shadow-denials(/summary)（仅老板 / 技术管理员）。
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldQuestion } from "lucide-react";
import { rbacApi } from "@/services/rbac";
import { roleEmoji } from "./rbacMeta";

export function ShadowDenialsPanel() {
  const [open, setOpen] = useState(false);
  const sumQ = useQuery({
    queryKey: ["rbac", "shadow", "summary"],
    queryFn: () => rbacApi.shadowDenialsSummary(),
    enabled: open,
  });
  const listQ = useQuery({
    queryKey: ["rbac", "shadow", "list"],
    queryFn: () => rbacApi.shadowDenials({ limit: 100 }),
    enabled: open,
  });

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="shadow-denials-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-5 py-3 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
          <ShieldQuestion className="h-4 w-4 text-amber-600" /> 权限影子日志
          <span className="font-normal text-slate-400">· 按新权限点本该被拦、但这次照旧放行的请求（切到「按权限点真拦」前对账用）</span>
        </span>
        <span className="text-[12px] text-blue-600">{open ? "收起" : "展开"}</span>
      </button>

      {open && (
        <div className="space-y-4 border-t border-slate-100 px-5 py-4">
          {(sumQ.isLoading || listQ.isLoading) && <p className="text-[12px] text-slate-400">加载中…</p>}
          {(sumQ.isError || listQ.isError) && <p className="text-[12px] text-red-600">影子日志加载失败（仅老板 / 技术管理员可看）</p>}

          {sumQ.data && (
            <div>
              <p className="mb-1.5 text-[12px] font-semibold text-slate-700">按「角色 × 权限点」汇总</p>
              {sumQ.data.length === 0 ? (
                <p className="text-[12px] text-slate-400">暂无记录——说明目前没有任何请求会被新权限点拦下。</p>
              ) : (
                <table className="w-full text-[12px]">
                  <thead className="text-left text-slate-500">
                    <tr><th className="py-1">角色</th><th>缺的权限点</th><th className="text-right">次数</th><th className="text-right">最近</th></tr>
                  </thead>
                  <tbody>
                    {sumQ.data.map((r) => (
                      <tr key={`${r.role_code}:${r.permission_code}`} className="border-t border-slate-50">
                        <td className="py-1">{roleEmoji(r.role_code)} {r.role_code}</td>
                        <td>{r.permission_label_cn ?? r.permission_code} <code className="text-[10px] text-slate-400">{r.permission_code}</code></td>
                        <td className="text-right font-medium">{r.count}</td>
                        <td className="text-right text-slate-400">{new Date(r.last_at).toLocaleString("zh-CN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {listQ.data && listQ.data.length > 0 && (
            <div>
              <p className="mb-1.5 text-[12px] font-semibold text-slate-700">最近 {listQ.data.length} 条</p>
              <ul className="divide-y divide-slate-50 text-[12px]">
                {listQ.data.map((x) => (
                  <li key={x.id} className="flex flex-wrap items-center gap-x-2 py-1.5">
                    <span className="font-medium text-slate-800">{x.user_name ?? x.user_id.slice(0, 8)}</span>
                    <span className="text-slate-400">{x.role_codes.join("、")}</span>
                    <code className="text-slate-600">{x.method} {x.path}</code>
                    <span className="text-amber-700">缺：{x.permission_label_cn ?? x.permission_code}</span>
                    <span className="ml-auto text-[11px] text-slate-400">{new Date(x.created_at).toLocaleString("zh-CN")}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
